import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Form, Input, Space, Typography, Row } from 'antd';
import { RcFile, UploadChangeParam, UploadProps } from 'antd/es/upload';
import { AxiosProgressEvent } from 'axios';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useUpdateComponent } from '@/shared/api';
import { ProgressStatus } from '@/shared/types';
import { Col } from '@/shared/ui';
import { captureError } from '@/shared/utils';
import { ImageLibraryModal } from '@/widgets/image-library';
import { ImagePreview } from '@/widgets/image-library/ImagePreview';
import { ImageEditFormUpload } from './ImageEditFormUpload';
import useComponentFileUpload from './useComponentFileUpload';

enum ImageType {
  Library = 'library',
  Imported = 'imported',
  Uploaded = 'uploaded',
}

interface ImageState {
  id?: string;
  url?: string;
  type?: ImageType;
  file?: RcFile;
}

interface ImageUploadState {
  progress?: number;
  progressStatus?: ProgressStatus;
}

const { Text } = Typography;

export const ImageEditForm = forwardRef((_, ref) => {
  const queryClient = useQueryClient();
  const {
    selectedComponent,
    selectedStep,
    proc,
    setSelectedComponent,
    editFormLoading,
    setEditFormLoading,
  } = useProcContext();
  const { showNotification } = useNotificationContext();

  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<ImageState | null>();
  const [uploadState, setUploadState] = useState<ImageUploadState>();

  const { mutate: updateComponent, isLoading: updateComponentLoading } =
    useUpdateComponent();
  const { uploadFile } = useComponentFileUpload({ procId: proc?.id || null });

  const handleUploadChange: UploadProps['onChange'] = ({
    file,
  }: UploadChangeParam) => {
    setUploadState({ progress: 0 });
    if (file.status === 'uploading') {
      setEditFormLoading(true);
      return;
    }
    if (file.status === 'done') {
      setEditFormLoading(false);
      setCurrentImage({
        url: file.response.url,
        type: ImageType.Uploaded,
      });
    }
  };

  const onUploadProgress = ({ loaded, total }: AxiosProgressEvent) => {
    const progress = Math.floor((loaded * 100) / total!);
    setUploadState((curr) => ({ ...curr, progress }));
  };

  const onSubmit = useCallback(async () => {
    setEditFormLoading(true);
    let fileData;

    if (currentImage?.type === ImageType.Uploaded && currentImage.file) {
      fileData = await uploadFile({
        file: currentImage.file,
        onUploadProgress,
        onUploadError: () => {
          setUploadState({ progressStatus: 'exception' });
          setEditFormLoading(false);
        },
      });
    } else if (currentImage?.type === ImageType.Library) {
      fileData = { value: currentImage.url, mediaAssetId: currentImage.id };
    } else if (currentImage?.type === ImageType.Imported) {
      fileData = { value: currentImage.url };
    }
    if (!proc?.id || !selectedStep?.id || !selectedComponent?.id) {
      return null;
    }
    updateComponent(
      {
        pathParams: { procId: proc.id, stepId: selectedStep.id },
        body: {
          id: selectedComponent?.id,
          component: selectedComponent?.component,
          componentName: selectedComponent?.componentName,
          ...fileData,
        },
      },
      {
        onSuccess: () => {
          setSelectedComponent(undefined);
          queryClient.invalidateQueries({
            queryKey: ['procs', proc?.id],
          });
        },
        onError: (error: UpdateComponentError) => {
          showNotification({
            type: 'error',
            message: 'Update error',
            description: error?.payload.toString(),
          });
          captureError(error, {
            module: 'image_edit_form_on_error_update_component',
            action: 'update_component',
          });
        },
      },
    );
  }, [
    currentImage,
    proc?.id,
    queryClient,
    selectedComponent,
    selectedStep?.id,
    setEditFormLoading,
    setSelectedComponent,
    showNotification,
    updateComponent,
    uploadFile,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submit: onSubmit,
      isLoading: updateComponentLoading || editFormLoading,
    }),
    [onSubmit, updateComponentLoading, editFormLoading],
  );

  useEffect(() => {
    setUploadState({});
    if (selectedComponent.fileKey) {
      // Handle uploaded image
      setCurrentImage({
        url: selectedComponent.value,
        type: ImageType.Uploaded,
      });
    } else if (selectedComponent.mediaAssetId) {
      // Handle library image URL
      setCurrentImage({
        url: selectedComponent.value,
        id: selectedComponent.mediaAssetId,
        type: ImageType.Library,
      });
    } else {
      // Handle imported URL
      setCurrentImage({
        url: selectedComponent.value,
        type: ImageType.Imported,
      });
    }
  }, [selectedComponent]);

  const handleLibrarySelect = (url: string, id: string) => {
    setCurrentImage({ url, id, type: ImageType.Library });
    setIsLibraryModalOpen(false);
  };

  return (
    <Form
      name="imageEditForm"
      layout="vertical"
      initialValues={{ hyperlink: selectedComponent?.hyperlink }}
      style={{ width: '100%' }}
    >
      <Form.Item label="Select from library">
        <Col alignItems="flex-start">
          <Text
            style={{
              fontSize: 11,
              marginTop: -7,
              marginBottom: 10,
              opacity: 0.7,
            }}
          >
            Select an image from the extracted images from the selected
            documents
          </Text>
          <Row justify="space-between" style={{ width: '100%' }}>
            <Button
              onClick={() => setIsLibraryModalOpen(true)}
              style={{ marginTop: 10 }}
            >
              Open library
            </Button>
          </Row>
        </Col>
        {currentImage?.type === ImageType.Library && (
          <ImagePreview
            src={currentImage?.url}
            isLoading={editFormLoading}
            showProgress={Boolean(uploadState?.progress)}
            onClear={() => setCurrentImage(null)}
            {...uploadState}
          />
        )}
      </Form.Item>
      <Divider plain>OR</Divider>
      <Form.Item label="Upload">
        <ImageEditFormUpload
          file={currentImage?.file}
          onChange={handleUploadChange}
          onBeforeUpload={(file) =>
            setCurrentImage({ file, type: ImageType.Uploaded })
          }
          onRemove={() => {
            setCurrentImage(null);
            setUploadState({ progress: 0 });
          }}
        />
        {currentImage?.type === ImageType.Uploaded && (
          <ImagePreview
            src={currentImage?.url}
            isLoading={editFormLoading}
            showProgress={Boolean(uploadState?.progress)}
            onClear={() => setCurrentImage(null)}
            {...uploadState}
          />
        )}
      </Form.Item>
      <Divider plain>OR</Divider>
      <Form.Item label="Import image from URL">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            data-testid="image-url-input"
            value={
              currentImage?.type === ImageType.Imported ? currentImage.url : ''
            }
            onChange={({ target }) => {
              setCurrentImage({
                url: target.value,
                type: ImageType.Imported,
              });
            }}
          />
          {currentImage?.type === ImageType.Imported && (
            <ImagePreview
              src={currentImage?.url}
              isLoading={editFormLoading}
              showProgress={false}
              onClear={() => setCurrentImage(null)}
            />
          )}
        </Space>
      </Form.Item>
      <ImageLibraryModal
        open={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onSelect={handleLibrarySelect}
        fileIds={proc?.fileIds}
      />
    </Form>
  );
});

ImageEditForm.displayName = 'ImageEditForm';
